import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axiosInstance from "../../../interceptors";
import { baseUrl } from "../../../config/config";

const DEFAULT_ATTRIBUTES = [
  { name: "Technical skills", description: "Depth and accuracy of technical knowledge" },
  { name: "Communication", description: "Clarity, listening, and articulation" },
  { name: "Problem solving", description: "Approach to ambiguous or novel problems" },
  { name: "Culture fit", description: "Alignment with team values and ways of working" },
];

const RECS = [
  { value: "STRONG_YES", label: "Strong yes", color: "bg-emerald-600 text-white", border: "border-emerald-600" },
  { value: "YES", label: "Yes", color: "bg-emerald-100 text-emerald-700", border: "border-emerald-400" },
  { value: "NO", label: "No", color: "bg-red-100 text-red-700", border: "border-red-400" },
  { value: "STRONG_NO", label: "Strong no", color: "bg-red-600 text-white", border: "border-red-600" },
];

export default function ScorecardSubmit() {
  const { candidateId, jobId, roundId } = useParams();
  const [search] = useSearchParams();
  const candidateName = search.get("candidateName") || `Candidate #${candidateId}`;
  const jobTitle = search.get("jobTitle") || `Job #${jobId}`;
  const nav = useNavigate();

  const [ratings, setRatings] = useState(
    DEFAULT_ATTRIBUTES.map((a) => ({ attributeName: a.name, rating: 0, notes: "" }))
  );
  const [recommendation, setRecommendation] = useState(null);
  const [strengths, setStrengths] = useState("");
  const [concerns, setConcerns] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = "Submit scorecard - Ample Hire";
  }, []);

  const updateRating = (idx, field, value) => {
    setRatings((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  const overallRating = (() => {
    const rated = ratings.filter((r) => r.rating > 0);
    if (rated.length === 0) return 0;
    return Math.round(rated.reduce((s, r) => s + r.rating, 0) / rated.length);
  })();

  const submit = async () => {
    if (!recommendation) {
      setError("Please pick a recommendation.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await axiosInstance.post(`${baseUrl}/job-posting/scorecards/submit`, {
        candidateId: Number(candidateId),
        jobId: Number(jobId),
        roundId: roundId ? Number(roundId) : null,
        attributeRatings: JSON.stringify(ratings),
        overallRating,
        recommendation,
        strengths,
        concerns,
        status: "SUBMITTED",
      });
      setDone(true);
    } catch (e) {
      setError(e?.message || "Failed to submit scorecard");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="page-enter max-w-2xl mx-auto py-12">
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
          <div className="text-5xl mb-3">✓</div>
          <h2 className="text-xl font-semibold text-vf-base">Scorecard submitted</h2>
          <p className="text-sm text-vf-muted mt-2">
            Thank you. Your feedback for {candidateName} has been recorded.
          </p>
          <div className="flex gap-2 justify-center mt-6">
            <button
              onClick={() => nav(`/admin/candidate-report?candidateId=${candidateId}`)}
              className="px-4 py-2 rounded bg-vf-primary text-white text-sm"
            >
              View candidate report
            </button>
            <button
              onClick={() => nav(-1)}
              className="px-4 py-2 rounded border border-slate-300 text-sm"
            >
              Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-enter max-w-3xl mx-auto py-6">
      <div className="mb-5">
        <button onClick={() => nav(-1)} className="text-xs text-vf-muted hover:text-vf-base mb-1">
          ← Back
        </button>
        <h1 className="text-2xl font-semibold text-vf-base">Submit interview feedback</h1>
        <p className="text-sm text-vf-muted mt-1">
          {candidateName} · {jobTitle}
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-200">
        {DEFAULT_ATTRIBUTES.map((attr, idx) => (
          <div key={attr.name} className="p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-sm font-medium text-vf-base">{attr.name}</div>
                <div className="text-xs text-vf-muted mt-0.5">{attr.description}</div>
              </div>
              <div className="flex gap-1 shrink-0">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    onClick={() => updateRating(idx, "rating", n)}
                    className={`w-8 h-8 rounded text-sm font-medium border ${
                      ratings[idx].rating >= n
                        ? "bg-vf-primary text-white border-vf-primary"
                        : "bg-white text-vf-muted border-slate-300 hover:border-slate-400"
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <textarea
              value={ratings[idx].notes}
              onChange={(e) => updateRating(idx, "notes", e.target.value)}
              placeholder="Notes for this attribute (optional)"
              className="mt-3 w-full px-3 py-2 border border-slate-200 rounded text-sm resize-none"
              rows={2}
            />
          </div>
        ))}

        <div className="p-5">
          <div className="text-sm font-medium text-vf-base mb-3">
            Overall recommendation <span className="text-red-500">*</span>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {RECS.map((r) => (
              <button
                key={r.value}
                onClick={() => setRecommendation(r.value)}
                className={`px-3 py-2 rounded border text-sm font-medium ${
                  recommendation === r.value ? r.color + " " + r.border : "bg-white text-vf-base border-slate-300 hover:border-slate-400"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-vf-base">Strengths</label>
            <textarea
              value={strengths}
              onChange={(e) => setStrengths(e.target.value)}
              placeholder="What did the candidate do well?"
              className="mt-1 w-full px-3 py-2 border border-slate-200 rounded text-sm resize-none"
              rows={4}
            />
          </div>
          <div>
            <label className="text-sm font-medium text-vf-base">Concerns</label>
            <textarea
              value={concerns}
              onChange={(e) => setConcerns(e.target.value)}
              placeholder="Anything that gave you pause?"
              className="mt-1 w-full px-3 py-2 border border-slate-200 rounded text-sm resize-none"
              rows={4}
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="mt-3 p-3 rounded bg-red-50 text-red-700 text-sm border border-red-200">
          {error}
        </div>
      )}

      <div className="flex justify-between items-center mt-5">
        <div className="text-xs text-vf-muted">
          Average rating: <span className="font-semibold text-vf-base">{overallRating}/5</span>
        </div>
        <div className="flex gap-2">
          <button onClick={() => nav(-1)} className="px-4 py-2 rounded border border-slate-300 text-sm">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            className="px-4 py-2 rounded bg-vf-primary text-white text-sm disabled:opacity-50"
          >
            {submitting ? "Submitting…" : "Submit feedback"}
          </button>
        </div>
      </div>
    </div>
  );
}
